/**
 * Procedural sound.
 *
 * No audio files ship with the project, so every effect is synthesised with
 * the Web Audio API: filtered noise bursts for footsteps and digging, short
 * FM tones for pickups and note blocks, and a low rumble for explosions. Not a
 * replacement for real foley, but it makes the world feel responsive and it
 * costs nothing to distribute.
 */

interface SoundSpec {
  /** 'noise' shapes filtered white noise; 'tone' shapes an oscillator. */
  kind: 'noise' | 'tone';
  duration: number;
  /** Filter centre (noise) or base frequency (tone), in Hz. */
  frequency: number;
  /** Frequency at the end of the sound, for sweeps. */
  endFrequency?: number;
  q?: number;
  gain: number;
  attack?: number;
  type?: OscillatorType;
}

const SOUNDS: Record<string, SoundSpec> = {
  dig_stone: { kind: 'noise', duration: 0.16, frequency: 1400, q: 1.2, gain: 0.35 },
  dig_deepslate: { kind: 'noise', duration: 0.18, frequency: 1100, q: 1.4, gain: 0.35 },
  dig_wood: { kind: 'noise', duration: 0.15, frequency: 800, q: 2.0, gain: 0.35 },
  dig_gravel: { kind: 'noise', duration: 0.2, frequency: 600, q: 0.8, gain: 0.35 },
  dig_sand: { kind: 'noise', duration: 0.22, frequency: 2600, q: 0.5, gain: 0.28 },
  dig_grass: { kind: 'noise', duration: 0.18, frequency: 2000, q: 0.7, gain: 0.28 },
  dig_glass: { kind: 'noise', duration: 0.12, frequency: 5200, q: 3.0, gain: 0.4 },
  dig_metal: { kind: 'noise', duration: 0.14, frequency: 3200, q: 4.0, gain: 0.35 },
  dig_wool: { kind: 'noise', duration: 0.18, frequency: 500, q: 0.6, gain: 0.25 },
  dig_snow: { kind: 'noise', duration: 0.16, frequency: 3000, q: 0.6, gain: 0.22 },
  dig_slime: { kind: 'tone', duration: 0.16, frequency: 320, endFrequency: 120, gain: 0.3, type: 'sine' },
  dig_ladder: { kind: 'noise', duration: 0.14, frequency: 900, q: 2.0, gain: 0.3 },

  place_stone: { kind: 'noise', duration: 0.1, frequency: 1200, q: 1.5, gain: 0.3 },
  place_wood: { kind: 'noise', duration: 0.1, frequency: 700, q: 2.0, gain: 0.3 },
  place_gravel: { kind: 'noise', duration: 0.12, frequency: 550, q: 1.0, gain: 0.3 },
  place_sand: { kind: 'noise', duration: 0.14, frequency: 2400, q: 0.6, gain: 0.24 },
  place_grass: { kind: 'noise', duration: 0.1, frequency: 1800, q: 0.8, gain: 0.24 },
  place_glass: { kind: 'noise', duration: 0.09, frequency: 4800, q: 3.0, gain: 0.32 },
  place_metal: { kind: 'noise', duration: 0.1, frequency: 3000, q: 4.0, gain: 0.3 },
  place_wool: { kind: 'noise', duration: 0.12, frequency: 450, q: 0.6, gain: 0.22 },
  place_snow: { kind: 'noise', duration: 0.1, frequency: 2800, q: 0.6, gain: 0.2 },
  place_deepslate: { kind: 'noise', duration: 0.11, frequency: 1000, q: 1.5, gain: 0.3 },
  place_slime: { kind: 'tone', duration: 0.14, frequency: 260, endFrequency: 160, gain: 0.28, type: 'sine' },
  place_ladder: { kind: 'noise', duration: 0.1, frequency: 850, q: 2.0, gain: 0.28 },

  pickup: { kind: 'tone', duration: 0.09, frequency: 620, endFrequency: 980, gain: 0.16, type: 'triangle' },
  xp: { kind: 'tone', duration: 0.12, frequency: 880, endFrequency: 1320, gain: 0.14, type: 'sine' },
  hurt: { kind: 'tone', duration: 0.22, frequency: 220, endFrequency: 90, gain: 0.3, type: 'sawtooth' },
  hit: { kind: 'noise', duration: 0.1, frequency: 900, q: 1.0, gain: 0.34 },
  click: { kind: 'tone', duration: 0.05, frequency: 500, gain: 0.22, type: 'square' },
  door_open: { kind: 'tone', duration: 0.3, frequency: 180, endFrequency: 260, gain: 0.22, type: 'sawtooth' },
  door_close: { kind: 'tone', duration: 0.28, frequency: 260, endFrequency: 150, gain: 0.22, type: 'sawtooth' },
  explode: { kind: 'noise', duration: 1.1, frequency: 160, q: 0.4, gain: 0.7 },
  portal: { kind: 'tone', duration: 0.7, frequency: 90, endFrequency: 420, gain: 0.25, type: 'sine' },
  note: { kind: 'tone', duration: 0.5, frequency: 440, gain: 0.22, type: 'triangle' },
  eat: { kind: 'noise', duration: 0.12, frequency: 700, q: 1.2, gain: 0.2 },
  break_item: { kind: 'noise', duration: 0.25, frequency: 1600, q: 1.0, gain: 0.3 },
  fizz: { kind: 'noise', duration: 0.5, frequency: 3500, q: 0.4, gain: 0.3 },
  bow: { kind: 'tone', duration: 0.2, frequency: 900, endFrequency: 300, gain: 0.2, type: 'triangle' },
};

const MAX_DISTANCE = 32;

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private lastPlayed = new Map<string, number>();
  enabled = true;
  volume = 0.6;

  private ensure(): AudioContext | null {
    if (!this.enabled) return null;
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return this.ctx;
    }
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = this.volume;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;

      // One second of white noise, reused for every noise-based effect.
      const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
      return ctx;
    } catch {
      this.enabled = false;
      return null;
    }
  }

  play(
    name: string,
    x: number, y: number, z: number,
    volume: number, pitch: number,
    listenerX: number, listenerY: number, listenerZ: number,
  ): void {
    const spec = SOUNDS[name];
    if (!spec) return;
    const distance = Math.hypot(x - listenerX, y - listenerY, z - listenerZ);
    if (distance > MAX_DISTANCE) return;

    const ctx = this.ensure();
    if (!ctx || !this.master) return;

    // Rate-limit identical sounds so a piston array does not blow out the mix.
    const now = ctx.currentTime;
    const last = this.lastPlayed.get(name) ?? -1;
    if (now - last < 0.02) return;
    this.lastPlayed.set(name, now);

    const attenuation = Math.max(0, 1 - distance / MAX_DISTANCE) ** 1.5;
    const gain = ctx.createGain();
    const level = spec.gain * volume * attenuation;
    const attack = spec.attack ?? 0.005;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, level), now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + spec.duration);

    // Cheap stereo placement from the listener's left/right offset.
    const panner = ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, (x - listenerX) / MAX_DISTANCE));
    gain.connect(panner);
    panner.connect(this.master);

    if (spec.kind === 'noise' && this.noiseBuffer) {
      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = spec.frequency * pitch;
      filter.Q.value = spec.q ?? 1;
      source.connect(filter);
      filter.connect(gain);
      source.start(now, Math.random() * 0.5);
      source.stop(now + spec.duration + 0.02);
    } else {
      const osc = ctx.createOscillator();
      osc.type = spec.type ?? 'sine';
      osc.frequency.setValueAtTime(spec.frequency * pitch, now);
      if (spec.endFrequency) {
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(20, spec.endFrequency * pitch), now + spec.duration,
        );
      }
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + spec.duration + 0.02);
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume;
  }
}
